// RealEstate.jsx
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import "./RealEstate.css";

const NAV_OFFSET = 86;

// Works for Vite (VITE_*) and CRA (REACT_APP_*)
const API_BASE =
  (typeof import.meta !== "undefined" && import.meta.env?.VITE_CONTACT_API_URL) ||
  process.env.REACT_APP_CONTACT_API_URL ||
  "https://xjref277ki.execute-api.us-west-2.amazonaws.com";

// If your API expects a path like "/contact" or "/prod/contact", change it here:
const CONTACT_ENDPOINT = API_BASE;

const VideoEmbed = ({ id, title }) => (
  <div className="psoc-video">
    <iframe
      src={`https://www.youtube.com/embed/${id}`}
      title={title}
      frameBorder="0"
      loading="lazy"
      referrerPolicy="strict-origin-when-cross-origin"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      allowFullScreen
    />
  </div>
);

const Stat = ({ top, sub }) => (
  <div className="re-stat">
    <div className="re-statTop">{top}</div>
    <div className="re-statSub">{sub}</div>
  </div>
);

const RealEstate = () => {
  const contactRef = useRef(null);
  const galleryRef = useRef(null);

  const imageNames = useMemo(
    () => [
      "photo_1.jpg",
      "photo_2.jpg",
      "photo_3.jpg",
      "photo_4.jpg",
      "photo_5.jpg",
      "photo_6.jpg",
      "photo_7.jpg",
      "photo_8.jpg",
      "photo_9.jpg",
    ],
    []
  );

  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState({ state: "idle", message: "" }); // idle | sending | success | error
  const [subject, setSubject] = useState("Real Estate Quote");

  const openLightbox = (idx) => {
    setActiveIndex(idx);
    setIsLightboxOpen(true);
  };

  const closeLightbox = () => setIsLightboxOpen(false);

  const showNext = useCallback(() => {
    setActiveIndex((i) => (i + 1) % imageNames.length);
  }, [imageNames.length]);

  const showPrev = useCallback(() => {
    setActiveIndex((i) => (i - 1 + imageNames.length) % imageNames.length);
  }, [imageNames.length]);

  const scrollToRef = (ref) => {
    if (!ref?.current) return;
    const y = ref.current.getBoundingClientRect().top + window.scrollY - NAV_OFFSET;
    window.scrollTo({ top: y, behavior: "smooth" });
  };

  // Keyboard controls + scroll lock while lightbox is open
  useEffect(() => {
    if (!isLightboxOpen) return;

    const onKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowRight") showNext();
      if (e.key === "ArrowLeft") showPrev();
    };

    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [isLightboxOpen, showNext, showPrev]);

  const safeJson = async (res) => {
    const text = await res.text();
    try {
      return text ? JSON.parse(text) : {};
    } catch {
      return { message: text };
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    const form = e.currentTarget;

    const formData = {
      name: form.name.value.trim(),
      email: form.email.value.trim(),
      phone: form.phone.value.trim(),
      subject: subject?.trim() || "Real Estate Quote",
      message: form.message.value.trim(),
      sourcePage: "Real Estate",
    };

    setSubmitting(true);
    setStatus({ state: "sending", message: "Sending…" });

    try {
      const res = await fetch(CONTACT_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      const data = await safeJson(res);

      if (!res.ok) {
        const msg = data?.message || "There was an issue submitting the form.";
        throw new Error(msg);
      }

      setStatus({ state: "success", message: data?.message || "Sent — we’ll get back to you soon." });
      form.reset();
      setSubject("Real Estate Quote");
    } catch (err) {
      console.error(err);
      setStatus({
        state: "error",
        message: err?.message || "There was an error submitting the form.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="psoc re">
      {/* HERO */}
      <section className="re-hero">
        <div className="psoc-container re-heroGrid">
          <div className="re-heroText">
            <p className="psoc-badge">Real Estate • Walkthroughs • Drone</p>
            <h1 className="psoc-h1">
              Real Estate
              <br />
              Video + Photo Tours
            </h1>
            <p className="psoc-lead">
              Buying and selling is already complicated — your listing video shouldn’t be.
              We create clean walkthroughs, crisp photos, and eye-catching drone shots that help homes stand out.
            </p>

            <div className="re-ctaRow">
              <button className="psoc-btn psoc-btnPrimary" type="button" onClick={() => scrollToRef(contactRef)}>
                Get a Free Estimate
              </button>
              <button className="psoc-btn psoc-btnSecondary" type="button" onClick={() => scrollToRef(galleryRef)}>
                View Gallery
              </button>
            </div>

            <div className="re-stats">
              <Stat top="Fast turnaround" sub="Quick delivery for listings" />
              <Stat top="High fidelity" sub="Clean image + smooth motion" />
              <Stat top="Drone available" sub="Licensed & insured" />
            </div>

            <p className="re-related">
              Want something else? We also do{" "}
              <a className="psoc-link" href="/video-production">video production</a>{" "}
              and <a className="psoc-link" href="/weddings">weddings</a>.
            </p>
          </div>

          <div className="re-heroMedia" aria-label="Featured walkthrough">
            <VideoEmbed id="HFHjQ1U9Hgc" title="Featured Real Estate Walkthrough" />
            <div className="re-heroCaption">A quick sample walkthrough — scroll for more.</div>
          </div>
        </div>
      </section>

      {/* WALKTHROUGHS */}
      <section className="psoc-section">
        <div className="psoc-container">
          <div className="psoc-sectionHead">
            <h2 className="psoc-h2">Professional Walkthroughs</h2>
            <p className="psoc-sub">
              Clean, comprehensive tours that help buyers shop confidently from anywhere.
            </p>
          </div>

          <div className="re-split">
            <div className="psoc-card re-copyCard">
              <h3 className="psoc-cardTitle">Beautiful listing videos</h3>
              <p className="psoc-cardText">
                Smooth pacing, clean framing, and edits that highlight the flow of the home without feeling rushed.
                We keep communication simple and deliver in formats that work for MLS, YouTube, and social.
              </p>

              <div className="re-pillRow">
                <span className="re-pill">2-minute options</span>
                <span className="re-pill">Agent intro available</span>
                <span className="re-pill">Royalty-free music</span>
              </div>

              <button
                className="psoc-btn psoc-btnSecondary"
                type="button"
                onClick={() => {
                  setSubject("Real Estate — Walkthrough Video");
                  scrollToRef(contactRef);
                }}
              >
                Ask about walkthroughs
              </button>
            </div>

            <VideoEmbed id="wXFl6xuVz6s" title="Real Estate Walkthrough Example" />
          </div>
        </div>
      </section>

      {/* MAKE IT STAND OUT */}
      <section className="psoc-section psoc-sectionAlt">
        <div className="psoc-container">
          <div className="re-split re-splitReverse">
            <div className="re-photoCard">
              <img src="/real_estate/dinning_room.jpg" alt="Dining room" className="re-photo" loading="lazy" />
            </div>

            <div className="psoc-card re-copyCard">
              <h3 className="psoc-cardTitle">Make your home stand out</h3>
              <p className="psoc-cardText">
                Marketing is the art of differentiation — our goal is to represent your listing accurately while making it feel
                inviting, bright, and worth a showing.
              </p>

              <div className="re-miniGrid">
                <div className="re-mini">
                  <div className="re-miniTop">Crisp composition</div>
                  <div className="re-miniSub">Angles that flatter rooms</div>
                </div>
                <div className="re-mini">
                  <div className="re-miniTop">Realistic color</div>
                  <div className="re-miniSub">True-to-life presentation</div>
                </div>
                <div className="re-mini">
                  <div className="re-miniTop">Buyer-friendly pacing</div>
                  <div className="re-miniSub">Easy to understand layout</div>
                </div>
                <div className="re-mini">
                  <div className="re-miniTop">Multiple exports</div>
                  <div className="re-miniSub">Web + social formats</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* DRONE */}
      <section className="psoc-section">
        <div className="psoc-container">
          <div className="psoc-sectionHead">
            <h2 className="psoc-h2">Drone Video + Photography</h2>
            <p className="psoc-sub">
              Aerial shots that show the property, neighborhood, and curb appeal in a way ground footage can’t.
            </p>
          </div>

          <div className="re-split">
            <div className="re-photoCard">
              <img src="/real_estate/house_drone.jpg" alt="Drone view of home" className="re-photo" loading="lazy" />
            </div>

            <div className="psoc-card re-copyCard">
              <h3 className="psoc-cardTitle">A unique perspective that catches eyes</h3>
              <p className="psoc-cardText">
                We’re licensed to fly and love giving buyers a clean aerial view that instantly boosts perceived value and context.
              </p>

              <div className="re-pillRow">
                <span className="re-pill">Exterior context</span>
                <span className="re-pill">Roof/lot visibility</span>
                <span className="re-pill">Neighborhood feel</span>
              </div>

              <button
                className="psoc-btn psoc-btnPrimary"
                type="button"
                onClick={() => {
                  setSubject("Real Estate — Drone Add-on");
                  scrollToRef(contactRef);
                }}
              >
                Ask about drone coverage
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* GALLERY */}
      <section className="psoc-section psoc-sectionAlt" ref={galleryRef} id="gallery">
        <div className="psoc-container">
          <div className="psoc-sectionHead re-sectionHeadRow">
            <div>
              <h2 className="psoc-h2">Photo Gallery</h2>
              <p className="psoc-sub">Tap any photo to view full size.</p>
            </div>

            <button className="psoc-btn psoc-btnGhost" type="button" onClick={() => openLightbox(0)}>
              Open slideshow
            </button>
          </div>

          <div className="re-grid">
            {imageNames.map((img, idx) => (
              <button
                key={img}
                className="re-gridBtn"
                onClick={() => openLightbox(idx)}
                aria-label={`Open photo ${idx + 1} of ${imageNames.length}`}
                type="button"
              >
                <img
                  src={`/real_estate/${img}`}
                  alt={`Property ${idx + 1}`}
                  className="re-gridImg"
                  loading="lazy"
                  draggable="false"
                />
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="psoc-section">
        <div className="psoc-container">
          <div className="psoc-sectionHead">
            <h2 className="psoc-h2">Pricing</h2>
            <p className="psoc-sub">Simple, transparent pricing with flexible add-ons.</p>
          </div>

          <div className="re-priceGrid">
            <div className="re-priceCard">
              <div className="re-priceTop">
                <h3 className="re-priceTitle">Standard Package</h3>
                <div className="re-price">$550</div>
              </div>

              <ul className="re-list">
                <li>Two-minute video walkthrough</li>
                <li>Stock music included</li>
                <li>Introduction of agent/agency</li>
                <li>Drone footage</li>
                <li>Professional voice-over</li>
              </ul>

              <button
                className="psoc-btn psoc-btnPrimary"
                type="button"
                onClick={() => {
                  setSubject("Real Estate — Standard Package");
                  scrollToRef(contactRef);
                }}
              >
                Request this package
              </button>
            </div>

            <div className="re-addonsCard">
              <div className="re-addonsTitle">Additional Services</div>
              <div className="re-addonsGrid">
                <div className="re-addon">360 walkthrough: $300 + $0.10 / sq ft</div>
                <div className="re-addon">Evening / sunset shoot: $75</div>
                <div className="re-addon">4K video: $50</div>
                <div className="re-addon">Add a minute of video: $50</div>
                <div className="re-addon">Photos: $300</div>
                <div className="re-addon">Custom logo animation: $300</div>
                <div className="re-addon">Titles + captions: $50</div>
                <div className="re-addon">Travel 20+ miles from 80906: $25</div>
                <div className="re-addon">Travel 40+ miles from 80906: $50</div>
              </div>

              <button
                className="psoc-btn psoc-btnSecondary"
                type="button"
                onClick={() => {
                  setSubject("Real Estate — Package + Add-ons");
                  scrollToRef(contactRef);
                }}
              >
                Ask about add-ons
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section className="psoc-section psoc-sectionAlt" ref={contactRef} id="contact">
        <div className="psoc-container">
          <div className="psoc-sectionHead">
            <h2 className="psoc-h2">Get a free estimate</h2>
            <p className="psoc-sub">
              Tell us the address (or general area), square footage, what you want (video, photos, drone), and your timeline.
            </p>
          </div>

          <div className="psoc-contactCard">
            <form onSubmit={handleSubmit} className="psoc-form">
              <div className="psoc-formGrid">
                <div className="psoc-field">
                  <label className="psoc-label" htmlFor="name">Name</label>
                  <input id="name" name="name" type="text" placeholder="Name" required autoComplete="name" />
                </div>

                <div className="psoc-field">
                  <label className="psoc-label" htmlFor="email">Email</label>
                  <input id="email" name="email" type="email" placeholder="Email" required autoComplete="email" />
                </div>

                <div className="psoc-field">
                  <label className="psoc-label" htmlFor="phone">Phone</label>
                  <input id="phone" name="phone" type="tel" placeholder="Phone (optional)" autoComplete="tel" />
                </div>

                <div className="psoc-field">
                  <label className="psoc-label" htmlFor="subject">Subject</label>
                  <input
                    id="subject"
                    name="subject"
                    type="text"
                    placeholder="Subject"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                  />
                </div>

                <div className="psoc-field psoc-fieldFull">
                  <label className="psoc-label" htmlFor="message">Message</label>
                  <textarea
                    id="message"
                    name="message"
                    placeholder="Address/area, sq ft, deliverables (video/photos/drone), and timeline…"
                    required
                    rows={7}
                  />
                </div>
              </div>

              <div className="psoc-formFooter">
                <button className="psoc-btn psoc-btnPrimary" type="submit" disabled={submitting}>
                  {submitting ? "Sending…" : "Submit"}
                </button>

                <div className="psoc-status" aria-live="polite">
                  {status.state !== "idle" && (
                    <span className={`psoc-statusMsg psoc-${status.state}`}>{status.message}</span>
                  )}
                </div>
              </div>
            </form>

            <p className="psoc-emailNote">
              Or email us directly at{" "}
              <a className="psoc-link" href="mailto:productionstudioofcolorado@gmail.com">
                productionstudioofcolorado@gmail.com
              </a>
            </p>
          </div>
        </div>
      </section>

      {/* LIGHTBOX */}
      {isLightboxOpen && (
        <div
          className="re-lbBackdrop"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeLightbox();
          }}
          role="dialog"
          aria-modal="true"
          aria-label="Photo viewer"
        >
          <div className="re-lightbox">
            <button className="re-lbClose" onClick={closeLightbox} aria-label="Close gallery" type="button">
              ×
            </button>

            <button className="re-lbNav re-lbPrev" onClick={showPrev} aria-label="Previous photo" type="button">
              ‹
            </button>

            <img
              src={`/real_estate/${imageNames[activeIndex]}`}
              alt={`Property ${activeIndex + 1}`}
              className="re-lbImg"
              draggable="false"
            />

            <button className="re-lbNav re-lbNext" onClick={showNext} aria-label="Next photo" type="button">
              ›
            </button>

            <div className="re-lbCaption">
              {activeIndex + 1} / {imageNames.length}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RealEstate;
